import { faQuestion, type IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { computed, unref, type MaybeRef } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useDisplayBadgeClass,
  type DisplayBadgeColor,
  type DisplayBadgeType,
} from '../style/useDisplayBadgeClass';

export interface UseBadgeInfoObjectOptions<T extends string | number> {
  i18nPrefix: string;
  colorMap: Record<T, DisplayBadgeColor>;
  iconMap: Record<T, IconDefinition>;
  defaultColor?: DisplayBadgeColor;
  defaultIcon?: IconDefinition;
  numericKeyFixedDigits?: number;
}

export interface BadgeInfo {
  title: string;
  description: string;
  color: DisplayBadgeColor;
  className: string;
  icon: IconDefinition;
}

export const useBadgeInfoObject = <T extends string | number>(
  {
    i18nPrefix,
    colorMap,
    iconMap,
    defaultColor,
    defaultIcon,
    numericKeyFixedDigits,
  }: UseBadgeInfoObjectOptions<T>,
  colorType: MaybeRef<DisplayBadgeType>,
  key?: MaybeRef<T>,
) => {
  const { t } = useI18n();
  const { getDisplayBadgeClass } = useDisplayBadgeClass(colorType);

  const getBadgeTitle = (key: T | undefined, infoType: 'title' | 'description') => {
    const statusKey =
      typeof key === 'number'
        ? key % 1 === 0
          ? key.toFixed(numericKeyFixedDigits ?? 0)
          : key.toString()
        : key;

    return t(`${i18nPrefix}['${statusKey}'].${infoType}`);
  };

  const getBadgeColor = (key: T | undefined): DisplayBadgeColor =>
    colorMap[key?.toString() as T] ?? defaultColor ?? 'neutral';

  const getBadgeClass = (key: T | undefined, type: DisplayBadgeType = 'badge') =>
    getDisplayBadgeClass(type, colorMap[key?.toString() as T] ?? defaultColor ?? 'neutral');

  const getBadgeIcon = (key: T | undefined): IconDefinition =>
    iconMap[key?.toString() as T] ?? defaultIcon ?? faQuestion;

  const getBadgeInfo = (key?: T): BadgeInfo => ({
    title: getBadgeTitle(key, 'title'),
    description: getBadgeTitle(key, 'description'),
    color: getBadgeColor(key),
    className: getBadgeClass(key, unref(colorType) ?? 'badge'),
    icon: getBadgeIcon(key),
  });

  const badgeInfo = computed(() => getBadgeInfo((unref(key) ?? -1) as T));

  return {
    badgeInfo,
    getBadgeTitle,
    getBadgeColor,
    getBadgeClass,
    getBadgeIcon,
    getBadgeInfo,
  };
};
